import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn, formatCompactNumber } from '@/lib/utils'
import { SECTION_COLORS } from '@/types/contextObservability'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Badge } from '@/components/ui/badge'

export interface CollapsibleSectionProps {
  sectionKey: string
  icon: React.ReactNode
  title: string
  tokens: number
  contextWindowSize: number
  defaultOpen?: boolean
  children: React.ReactNode
  className?: string
}

export function CollapsibleSection({
  sectionKey,
  icon,
  title,
  tokens,
  contextWindowSize,
  defaultOpen = false,
  children,
  className,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen)
  const pct = contextWindowSize > 0 ? (tokens / contextWindowSize) * 100 : 0
  const color = SECTION_COLORS[sectionKey] ?? 'hsl(240, 5%, 85%)'

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={cn('rounded-lg border bg-card', className)}>
      <CollapsibleTrigger className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors rounded-lg">
        <span
          className="flex h-7 w-7 items-center justify-center rounded-md text-white flex-shrink-0"
          style={{ backgroundColor: color }}
        >
          {icon}
        </span>

        <span className="flex-1 min-w-0">
          <span className="text-sm font-medium">{title}</span>
        </span>

        <div className="flex items-center gap-3 flex-shrink-0">
          <Badge variant="secondary" className="font-mono text-xs">
            {formatCompactNumber(tokens)} tok
          </Badge>

          <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden hidden sm:block">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }}
            />
          </div>

          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              open && 'rotate-90'
            )}
          />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="border-t px-4 py-3 overflow-hidden">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
